import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
    ArrowLeft, CalendarDays, Clock, CheckCircle2, XCircle,
    Loader2, Download, Flame, BookOpen,
} from "lucide-react";
import { toast } from "sonner";


function isoToday() {
    return new Date().toISOString().slice(0, 10);
}
function isoNDaysAgo(n) {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - (n - 1));
    return d.toISOString().slice(0, 10);
}

export default function AdminStudentAttendance() {
    const { studentId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    const [startDate, setStartDate] = useState(isoNDaysAgo(30));
    const [endDate, setEndDate] = useState(isoToday());
    const [programId, setProgramId] = useState("__all");

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { start: startDate, end: endDate };
            if (programId && programId !== "__all") params.program_id = programId;
            const { data: resp } = await api.get(
                `/admin/students/${studentId}/attendance`,
                { params },
            );
            setData(resp);
        } catch (e) {
            setError(e?.response?.data?.detail || "Could not load attendance.");
        } finally {
            setLoading(false);
        }
    }, [studentId, startDate, endDate, programId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const downloadCsv = async () => {
        try {
            const params = { start: startDate, end: endDate };
            if (programId && programId !== "__all") params.program_id = programId;
            const resp = await api.get(
                `/admin/students/${studentId}/attendance/export.csv`,
                { params, responseType: "blob" },
            );
            const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const safe = (data?.user?.name || data?.user?.email || "student").replace(/[^\w-]+/g, "_");
            a.download = `attendance-${safe}-${startDate}-to-${endDate}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Attendance exported.");
        } catch {
            toast.error("Could not export attendance.");
        }
    };

    const rollups = data?.rollups || {};
    const allPrograms = data?.programs || [];

    const presetRanges = useMemo(() => [
        { key: "7d",  label: "7 days",  start: isoNDaysAgo(7),  end: isoToday() },
        { key: "30d", label: "30 days", start: isoNDaysAgo(30), end: isoToday() },
        { key: "90d", label: "90 days", start: isoNDaysAgo(90), end: isoToday() },
    ], []);

    return (
        <div className="px-6 lg:px-10 py-8 max-w-6xl mx-auto" data-testid="admin-student-attendance-page">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/admin/students/${studentId}`)}
                        data-testid="attendance-back-btn"
                    >
                        <ArrowLeft className="h-4 w-4 mr-1.5" />
                        Back to dossier
                    </Button>
                </div>
                <Button
                    onClick={downloadCsv}
                    variant="outline"
                    className="gap-1.5"
                    disabled={loading || !data}
                    data-testid="attendance-export-btn"
                >
                    <Download className="h-4 w-4" />
                    Download CSV
                </Button>
            </div>

            <div className="mb-6">
                <h1 className="font-display text-3xl sm:text-4xl text-foreground">
                    Daily attendance
                </h1>
                {data?.user ? (
                    <p className="text-sm text-muted-foreground mt-1">
                        <strong>{data.user.name || data.user.email}</strong>
                        {data.user.student_id ? <span> · <code className="font-mono">{data.user.student_id}</code></span> : null}
                    </p>
                ) : null}
            </div>

            {/* Filters */}
            <Card className="p-4 mb-6 border-primary/10">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                        <Label className="text-xs text-muted-foreground">Start date</Label>
                        <Input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            data-testid="attendance-filter-start"
                        />
                    </div>
                    <div>
                        <Label className="text-xs text-muted-foreground">End date</Label>
                        <Input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            min={startDate}
                            data-testid="attendance-filter-end"
                        />
                    </div>
                    <div>
                        <Label className="text-xs text-muted-foreground">Program</Label>
                        <Select value={programId} onValueChange={setProgramId}>
                            <SelectTrigger data-testid="attendance-filter-program">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all">All programs</SelectItem>
                                {allPrograms.map((p) => (
                                    <SelectItem key={p.id} value={p.id}>{p.title || p.id}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex items-end gap-2">
                        {presetRanges.map((r) => (
                            <Button
                                key={r.key}
                                variant="outline"
                                size="sm"
                                onClick={() => { setStartDate(r.start); setEndDate(r.end); }}
                                data-testid={`attendance-preset-${r.key}`}
                            >
                                {r.label}
                            </Button>
                        ))}
                    </div>
                </div>
            </Card>

            {/* Rollup tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="attendance-rollups">
                <RollupTile
                    icon={<CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                    label="Days logged in"
                    value={rollups.days_logged_in ?? "—"}
                    testid="rollup-days-logged-in"
                />
                <RollupTile
                    icon={<XCircle className="h-4 w-4 text-rose-500" />}
                    label="Days absent"
                    value={rollups.days_absent ?? "—"}
                    testid="rollup-days-absent"
                />
                <RollupTile
                    icon={<Clock className="h-4 w-4 text-primary" />}
                    label="Total time studied"
                    value={rollups.total_hms || "00:00:00"}
                    testid="rollup-total-time"
                    mono
                />
                <RollupTile
                    icon={<Flame className="h-4 w-4 text-amber-600" />}
                    label="Longest streak"
                    value={rollups.longest_streak ?? "—"}
                    testid="rollup-longest-streak"
                />
            </div>

            {/* Table */}
            <Card className="overflow-hidden border-primary/10">
                {loading ? (
                    <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading attendance…
                    </div>
                ) : error ? (
                    <div className="p-10 text-center text-sm text-rose-600">
                        {error}
                    </div>
                ) : !data?.days?.length ? (
                    <div className="p-10 text-center text-sm text-muted-foreground">
                        No attendance data in this window.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="attendance-table">
                            <thead className="bg-muted/40 text-xs uppercase tracking-[0.16em] text-muted-foreground">
                                <tr>
                                    <th className="text-left px-4 py-3 font-semibold">Date</th>
                                    <th className="text-left px-4 py-3 font-semibold">Weekday</th>
                                    <th className="text-left px-4 py-3 font-semibold">Logged in</th>
                                    <th className="text-left px-4 py-3 font-semibold">Time studied</th>
                                    <th className="text-left px-4 py-3 font-semibold">Programs</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.days.map((d) => {
                                    const pre = d.status === "pre_enrolment";
                                    return (
                                        <tr
                                            key={d.date}
                                            className={`border-t border-border ${pre ? "opacity-50" : ""}`}
                                            data-testid={`attendance-row-${d.date}`}
                                        >
                                            <td className="px-4 py-2.5 font-mono text-xs">{d.date}</td>
                                            <td className="px-4 py-2.5">{d.weekday}</td>
                                            <td className="px-4 py-2.5">
                                                {pre ? (
                                                    <Badge variant="outline" className="text-[10px]">Pre-enrolment</Badge>
                                                ) : d.logged_in ? (
                                                    <Badge className="text-[10px] bg-emerald-600 hover:bg-emerald-600 text-white">
                                                        <CheckCircle2 className="h-3 w-3 mr-1" />
                                                        Yes
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-[10px] text-rose-600 border-rose-200">
                                                        <XCircle className="h-3 w-3 mr-1" />
                                                        No
                                                    </Badge>
                                                )}
                                            </td>
                                            <td className="px-4 py-2.5 font-mono text-xs">{d.time_studied_hms}</td>
                                            <td className="px-4 py-2.5 text-xs text-muted-foreground">
                                                {d.programs?.length ? (
                                                    <span className="inline-flex items-center gap-1 flex-wrap">
                                                        <BookOpen className="h-3 w-3" />
                                                        {d.programs.map((p) => p.title).join(", ")}
                                                    </span>
                                                ) : pre ? "—" : ""}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1.5">
                <CalendarDays className="h-3 w-3" />
                A day counts as &ldquo;logged in&rdquo; when the student is active for at
                least 10 minutes in the portal.
            </p>
        </div>
    );
}

function RollupTile({ icon, label, value, testid, mono = false }) {
    return (
        <Card className="p-4 border-primary/10" data-testid={testid}>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                {icon}
                {label}
            </div>
            <div className={`mt-2 text-2xl text-foreground ${mono ? "font-mono" : "font-semibold"}`}>
                {value}
            </div>
        </Card>
    );
}
